/**
 * Meme template definitions for the Canvas Editor.
 * Each template defines canvas size, background, and text placeholders.
 * Personal templates saved to localStorage.
 */

const STORAGE_KEY = 'blvx_canvas_templates';

// Built-in meme template library
export const MEME_TEMPLATES = [
  // --- Classic Memes ---
  {
    key: 'top-bottom',
    label: 'Top / Bottom',
    category: 'classic',
    aspect: '1:1',
    background: { type: 'solid', value: '#000000' },
    layers: [
      { type: 'text', text: 'TOP TEXT', x: 0.5, y: 0.12, fontSize: 72, fontFamily: 'Anton', fill: '#FFFFFF', textAlign: 'center', effect: 'outline', width: 0.9 },
      { type: 'text', text: 'BOTTOM TEXT', x: 0.5, y: 0.88, fontSize: 72, fontFamily: 'Anton', fill: '#FFFFFF', textAlign: 'center', effect: 'outline', width: 0.9 },
    ],
  },
  {
    key: 'caption-above',
    label: 'Caption Above',
    category: 'classic',
    aspect: '1:1',
    background: { type: 'solid', value: '#FFFFFF' },
    layers: [
      { type: 'text', text: 'When they said...', x: 0.5, y: 0.08, fontSize: 36, fontFamily: 'Inter', fill: '#000000', textAlign: 'center', width: 0.85 },
    ],
  },
  {
    key: 'drake',
    label: 'Two Panel',
    category: 'classic',
    aspect: '1:1',
    background: { type: 'solid', value: '#000000' },
    layers: [
      { type: 'text', text: '❌ NAH', x: 0.5, y: 0.25, fontSize: 56, fontFamily: 'Oswald', fill: '#FF3B30', textAlign: 'center', width: 0.8 },
      { type: 'text', text: '✅ THIS RIGHT HERE', x: 0.5, y: 0.75, fontSize: 56, fontFamily: 'Oswald', fill: '#34C759', textAlign: 'center', width: 0.8 },
    ],
  },
  {
    key: 'expanding-brain',
    label: 'Levels',
    category: 'classic',
    aspect: '9:16',
    background: { type: 'gradient', value: 'linear-gradient(135deg, #0a0a0a, #1a1a2e, #16213e)' },
    layers: [
      { type: 'text', text: 'Level 1 🧠', x: 0.5, y: 0.15, fontSize: 36, fontFamily: 'Inter', fill: '#8E8E93', textAlign: 'center', width: 0.8 },
      { type: 'text', text: 'Level 2 💡', x: 0.5, y: 0.4, fontSize: 40, fontFamily: 'Inter', fill: '#FFFFFF', textAlign: 'center', width: 0.8 },
      { type: 'text', text: 'Level 3 ✨', x: 0.5, y: 0.65, fontSize: 48, fontFamily: 'Oswald', fill: '#FFCC00', textAlign: 'center', width: 0.8 },
      { type: 'text', text: 'FINAL FORM 👑', x: 0.5, y: 0.88, fontSize: 56, fontFamily: 'Anton', fill: '#FF9500', textAlign: 'center', effect: 'glow', width: 0.8 },
    ],
  },

  // --- Quote & Inspiration ---
  {
    key: 'quote-minimal',
    label: 'Quote',
    category: 'quote',
    aspect: '1:1',
    background: { type: 'solid', value: '#000000' },
    layers: [
      { type: 'text', text: '"Your quote here"', x: 0.5, y: 0.45, fontSize: 44, fontFamily: 'Playfair Display', fill: '#FFFFFF', textAlign: 'center', fontWeight: '700', width: 0.75 },
      { type: 'text', text: '— Author', x: 0.5, y: 0.7, fontSize: 24, fontFamily: 'Inter', fill: '#8E8E93', textAlign: 'center', width: 0.6 },
    ],
  },
  {
    key: 'quote-gold',
    label: 'Gold Quote',
    category: 'quote',
    aspect: '1:1',
    background: { type: 'gradient', value: 'linear-gradient(135deg, #1a1000, #4a3000, #8b6914)' },
    layers: [
      { type: 'text', text: '"Speak your truth"', x: 0.5, y: 0.4, fontSize: 48, fontFamily: 'Dancing Script', fill: '#F5E6D3', textAlign: 'center', fontWeight: '700', width: 0.8 },
      { type: 'text', text: '✊🏾', x: 0.5, y: 0.65, fontSize: 64, fontFamily: 'Inter', fill: '#FFFFFF', textAlign: 'center', width: 0.3 },
    ],
  },
  {
    key: 'affirmation',
    label: 'Affirmation',
    category: 'quote',
    aspect: '9:16',
    background: { type: 'gradient', value: 'linear-gradient(135deg, #1a0030, #3a0060, #6a0dad)' },
    layers: [
      { type: 'text', text: 'I AM', x: 0.5, y: 0.3, fontSize: 80, fontFamily: 'Oswald', fill: '#FFFFFF', textAlign: 'center', charSpacing: 200, fontWeight: '700', width: 0.8 },
      { type: 'text', text: 'worthy of every blessing coming my way', x: 0.5, y: 0.5, fontSize: 36, fontFamily: 'Inter', fill: '#AF52DE', textAlign: 'center', width: 0.75 },
    ],
  },
  {
    key: 'wisdom',
    label: 'Wisdom',
    category: 'quote',
    aspect: '1:1',
    background: { type: 'solid', value: '#1a1a2e' },
    layers: [
      { type: 'text', text: '💭', x: 0.5, y: 0.25, fontSize: 72, fontFamily: 'Inter', fill: '#FFFFFF', textAlign: 'center', width: 0.3 },
      { type: 'text', text: 'Real talk...', x: 0.5, y: 0.55, fontSize: 40, fontFamily: 'Permanent Marker', fill: '#FFCC00', textAlign: 'center', width: 0.8 },
    ],
  },

  // --- Reaction / Hot Take ---
  {
    key: 'hot-take',
    label: 'Hot Take',
    category: 'reaction',
    aspect: '1:1',
    background: { type: 'gradient', value: 'linear-gradient(135deg, #1a0a00, #4a1a00, #8b3a00)' },
    layers: [
      { type: 'text', text: '🔥 HOT TAKE 🔥', x: 0.5, y: 0.2, fontSize: 40, fontFamily: 'Oswald', fill: '#FF9500', textAlign: 'center', charSpacing: 80, width: 0.9 },
      { type: 'text', text: 'Your spicy opinion here', x: 0.5, y: 0.55, fontSize: 36, fontFamily: 'Inter', fill: '#FFFFFF', textAlign: 'center', width: 0.8 },
    ],
  },
  {
    key: 'unpopular',
    label: 'Unpopular',
    category: 'reaction',
    aspect: '1:1',
    background: { type: 'solid', value: '#000000' },
    layers: [
      { type: 'text', text: 'UNPOPULAR OPINION', x: 0.5, y: 0.15, fontSize: 32, fontFamily: 'Oswald', fill: '#FF2D55', textAlign: 'center', charSpacing: 120, width: 0.9 },
      { type: 'text', text: 'But hear me out...', x: 0.5, y: 0.5, fontSize: 40, fontFamily: 'Inter', fill: '#FFFFFF', textAlign: 'center', width: 0.8 },
    ],
  },
  {
    key: 'facts',
    label: 'Facts',
    category: 'reaction',
    aspect: '1:1',
    background: { type: 'solid', value: '#000000' },
    layers: [
      { type: 'text', text: '📢 FACTS', x: 0.5, y: 0.2, fontSize: 64, fontFamily: 'Anton', fill: '#FFFFFF', textAlign: 'center', effect: 'shadow', width: 0.8 },
      { type: 'text', text: 'No cap detected', x: 0.5, y: 0.55, fontSize: 32, fontFamily: 'Inter', fill: '#34C759', textAlign: 'center', width: 0.7 },
    ],
  },
  {
    key: 'this-you',
    label: 'This You?',
    category: 'reaction',
    aspect: '1:1',
    background: { type: 'solid', value: '#FFFFFF' },
    layers: [
      { type: 'text', text: 'This you? 🤨', x: 0.5, y: 0.12, fontSize: 48, fontFamily: 'Inter', fill: '#000000', textAlign: 'center', fontWeight: '700', width: 0.8 },
    ],
  },

  // --- Story Templates ---
  {
    key: 'story-gradient',
    label: 'Story Vibe',
    category: 'story',
    aspect: '9:16',
    background: { type: 'gradient', value: 'linear-gradient(135deg, #001020, #003060, #0066aa)' },
    layers: [
      { type: 'text', text: 'YOUR STORY', x: 0.5, y: 0.45, fontSize: 64, fontFamily: 'Righteous', fill: '#FFFFFF', textAlign: 'center', charSpacing: 60, width: 0.8 },
      { type: 'text', text: 'starts here', x: 0.5, y: 0.55, fontSize: 28, fontFamily: 'Inter', fill: '#007AFF', textAlign: 'center', width: 0.6 },
    ],
  },
  {
    key: 'ama',
    label: 'AMA',
    category: 'story',
    aspect: '9:16',
    background: { type: 'gradient', value: 'linear-gradient(135deg, #1a0010, #4a0030, #8b1450)' },
    layers: [
      { type: 'text', text: 'ASK ME ANYTHING', x: 0.5, y: 0.35, fontSize: 56, fontFamily: 'Oswald', fill: '#FFFFFF', textAlign: 'center', charSpacing: 100, fontWeight: '700', width: 0.85 },
      { type: 'text', text: '⬇️ Drop your questions ⬇️', x: 0.5, y: 0.55, fontSize: 24, fontFamily: 'Inter', fill: '#FF2D55', textAlign: 'center', width: 0.8 },
    ],
  },
  {
    key: 'rating',
    label: 'Rate This',
    category: 'story',
    aspect: '9:16',
    background: { type: 'solid', value: '#000000' },
    layers: [
      { type: 'text', text: 'RATE THIS', x: 0.5, y: 0.25, fontSize: 56, fontFamily: 'Anton', fill: '#FFFFFF', textAlign: 'center', width: 0.7 },
      { type: 'text', text: '⭐⭐⭐⭐⭐', x: 0.5, y: 0.5, fontSize: 48, fontFamily: 'Inter', fill: '#FFFFFF', textAlign: 'center', width: 0.8 },
      { type: 'text', text: '1-10?', x: 0.5, y: 0.65, fontSize: 40, fontFamily: 'Oswald', fill: '#FFCC00', textAlign: 'center', width: 0.4 },
    ],
  },

  // --- Announcement ---
  {
    key: 'announcement',
    label: 'Announce',
    category: 'announce',
    aspect: '1:1',
    background: { type: 'gradient', value: 'linear-gradient(135deg, #111111, #333333, #555555)' },
    layers: [
      { type: 'text', text: '📣 ANNOUNCEMENT', x: 0.5, y: 0.2, fontSize: 36, fontFamily: 'Oswald', fill: '#FFCC00', textAlign: 'center', charSpacing: 80, width: 0.9 },
      { type: 'text', text: 'Big news dropping', x: 0.5, y: 0.5, fontSize: 44, fontFamily: 'Playfair Display', fill: '#FFFFFF', textAlign: 'center', fontWeight: '700', width: 0.8 },
      { type: 'text', text: 'Stay tuned 👀', x: 0.5, y: 0.75, fontSize: 28, fontFamily: 'Inter', fill: '#8E8E93', textAlign: 'center', width: 0.6 },
    ],
  },
  {
    key: 'event',
    label: 'Event',
    category: 'announce',
    aspect: '4:5',
    background: { type: 'gradient', value: 'linear-gradient(135deg, #001a00, #003a00, #006a00)' },
    layers: [
      { type: 'text', text: 'EVENT NAME', x: 0.5, y: 0.3, fontSize: 64, fontFamily: 'Bebas Neue', fill: '#FFFFFF', textAlign: 'center', charSpacing: 100, width: 0.85 },
      { type: 'text', text: 'Date & Time', x: 0.5, y: 0.5, fontSize: 28, fontFamily: 'Inter', fill: '#34C759', textAlign: 'center', width: 0.7 },
      { type: 'text', text: 'Location', x: 0.5, y: 0.6, fontSize: 24, fontFamily: 'Inter', fill: '#8E8E93', textAlign: 'center', width: 0.7 },
    ],
  },
  {
    key: 'promotion',
    label: 'Promo',
    category: 'announce',
    aspect: '1:1',
    background: { type: 'gradient', value: 'linear-gradient(135deg, #1a1000, #4a3000, #8b6914)' },
    layers: [
      { type: 'text', text: 'COMING SOON', x: 0.5, y: 0.35, fontSize: 52, fontFamily: 'Oswald', fill: '#F5E6D3', textAlign: 'center', charSpacing: 120, fontWeight: '700', width: 0.85 },
      { type: 'text', text: '🔔 Stay locked in', x: 0.5, y: 0.6, fontSize: 28, fontFamily: 'Inter', fill: '#D4A574', textAlign: 'center', width: 0.7 },
    ],
  },
  {
    key: 'blank',
    label: 'Blank',
    category: 'classic',
    aspect: '1:1',
    background: { type: 'solid', value: '#000000' },
    layers: [],
  },

  // --- Photo Templates (intentionally Black/brown community focused) ---
  {
    key: 'photo-queen',
    label: 'Queen Energy',
    category: 'photo',
    aspect: '1:1',
    thumbnail: 'https://images.unsplash.com/photo-1632765854612-9b02b6ec2b15?w=400&h=400&fit=crop&auto=format&q=80',
    background: { type: 'image', value: 'https://images.unsplash.com/photo-1632765854612-9b02b6ec2b15?w=1080&h=1080&fit=crop&auto=format&q=80' },
    layers: [
      { type: 'text', text: 'QUEEN ENERGY', x: 0.5, y: 0.2, fontSize: 52, fontFamily: 'Oswald', fill: '#FFFFFF', textAlign: 'center', charSpacing: 120, fontWeight: '700', width: 0.85, effect: 'shadow' },
      { type: 'text', text: 'Your caption here', x: 0.5, y: 0.82, fontSize: 28, fontFamily: 'Inter', fill: '#FFFFFF', textAlign: 'center', width: 0.8, effect: 'shadow' },
    ],
  },
  {
    key: 'photo-king',
    label: 'King Mode',
    category: 'photo',
    aspect: '1:1',
    thumbnail: 'https://images.unsplash.com/photo-1570158268183-d296b2892211?w=400&h=400&fit=crop&auto=format&q=80',
    background: { type: 'image', value: 'https://images.unsplash.com/photo-1570158268183-d296b2892211?w=1080&h=1080&fit=crop&auto=format&q=80' },
    layers: [
      { type: 'text', text: 'KING MODE', x: 0.5, y: 0.15, fontSize: 56, fontFamily: 'Anton', fill: '#FFFFFF', textAlign: 'center', charSpacing: 80, width: 0.9, effect: 'shadow' },
      { type: 'text', text: '"Your quote here"', x: 0.5, y: 0.8, fontSize: 26, fontFamily: 'Playfair Display', fill: '#F5E6D3', textAlign: 'center', fontWeight: '700', width: 0.8, effect: 'shadow' },
    ],
  },
  {
    key: 'photo-hustle',
    label: 'Hustle Mode',
    category: 'photo',
    aspect: '4:5',
    thumbnail: 'https://images.unsplash.com/photo-1648757766966-43d24bf7a264?w=400&h=500&fit=crop&auto=format&q=80',
    background: { type: 'image', value: 'https://images.unsplash.com/photo-1648757766966-43d24bf7a264?w=1080&h=1350&fit=crop&auto=format&q=80' },
    layers: [
      { type: 'text', text: 'HUSTLE MODE', x: 0.5, y: 0.12, fontSize: 48, fontFamily: 'Oswald', fill: '#FFCC00', textAlign: 'center', charSpacing: 100, fontWeight: '700', width: 0.9, effect: 'shadow' },
      { type: 'text', text: 'Built different. Moves different.', x: 0.5, y: 0.85, fontSize: 24, fontFamily: 'Inter', fill: '#FFFFFF', textAlign: 'center', width: 0.85, effect: 'shadow' },
    ],
  },
  {
    key: 'photo-drip',
    label: 'Drip Check',
    category: 'photo',
    aspect: '4:5',
    thumbnail: 'https://images.unsplash.com/photo-1527203561188-dae1bc1a417f?w=400&h=500&fit=crop&auto=format&q=80',
    background: { type: 'image', value: 'https://images.unsplash.com/photo-1527203561188-dae1bc1a417f?w=1080&h=1350&fit=crop&auto=format&q=80' },
    layers: [
      { type: 'text', text: 'DRIP CHECK ✨', x: 0.5, y: 0.1, fontSize: 44, fontFamily: 'Righteous', fill: '#FFFFFF', textAlign: 'center', width: 0.85, effect: 'shadow' },
    ],
  },
  {
    key: 'photo-golden',
    label: 'Golden Hour',
    category: 'photo',
    aspect: '9:16',
    thumbnail: 'https://images.unsplash.com/photo-1679508004557-52bfaec15fe1?w=400&h=711&fit=crop&auto=format&q=80',
    background: { type: 'image', value: 'https://images.unsplash.com/photo-1679508004557-52bfaec15fe1?w=1080&h=1920&fit=crop&auto=format&q=80' },
    layers: [
      { type: 'text', text: 'GOLDEN HOUR', x: 0.5, y: 0.35, fontSize: 60, fontFamily: 'Dancing Script', fill: '#FFD700', textAlign: 'center', fontWeight: '700', width: 0.8, effect: 'glow' },
      { type: 'text', text: 'Magic happens here', x: 0.5, y: 0.48, fontSize: 24, fontFamily: 'Inter', fill: '#FFFFFF', textAlign: 'center', width: 0.7, effect: 'shadow' },
    ],
  },
  {
    key: 'photo-manifest',
    label: 'Manifest',
    category: 'photo',
    aspect: '1:1',
    thumbnail: 'https://images.unsplash.com/photo-1522529599102-193c0d76b5b6?w=400&h=400&fit=crop&auto=format&q=80',
    background: { type: 'image', value: 'https://images.unsplash.com/photo-1522529599102-193c0d76b5b6?w=1080&h=1080&fit=crop&auto=format&q=80' },
    layers: [
      { type: 'text', text: 'MANIFEST IT', x: 0.5, y: 0.3, fontSize: 64, fontFamily: 'Oswald', fill: '#FFFFFF', textAlign: 'center', charSpacing: 100, fontWeight: '700', width: 0.85, effect: 'shadow' },
      { type: 'text', text: 'Speak it into existence', x: 0.5, y: 0.5, fontSize: 24, fontFamily: 'Inter', fill: '#E8D5B7', textAlign: 'center', width: 0.75, effect: 'shadow' },
    ],
  },
  {
    key: 'photo-squad',
    label: 'Squad Goals',
    category: 'photo',
    aspect: '9:16',
    thumbnail: 'https://images.unsplash.com/photo-1565884280295-98eb83e41c65?w=400&h=711&fit=crop&auto=format&q=80',
    background: { type: 'image', value: 'https://images.unsplash.com/photo-1565884280295-98eb83e41c65?w=1080&h=1920&fit=crop&auto=format&q=80' },
    layers: [
      { type: 'text', text: 'SQUAD GOALS', x: 0.5, y: 0.15, fontSize: 56, fontFamily: 'Anton', fill: '#FFFFFF', textAlign: 'center', width: 0.9, effect: 'shadow' },
      { type: 'text', text: '🔥 Tag your people 🔥', x: 0.5, y: 0.85, fontSize: 24, fontFamily: 'Inter', fill: '#FFD700', textAlign: 'center', width: 0.8, effect: 'shadow' },
    ],
  },
  {
    key: 'photo-celebrate',
    label: 'Celebrate',
    category: 'photo',
    aspect: '1:1',
    thumbnail: 'https://images.unsplash.com/photo-1587064712555-6e206484699b?w=400&h=400&fit=crop&auto=format&q=80',
    background: { type: 'image', value: 'https://images.unsplash.com/photo-1587064712555-6e206484699b?w=1080&h=1080&fit=crop&auto=format&q=80' },
    layers: [
      { type: 'text', text: '🎉 WE UP', x: 0.5, y: 0.18, fontSize: 56, fontFamily: 'Oswald', fill: '#FFFFFF', textAlign: 'center', charSpacing: 80, fontWeight: '700', width: 0.85, effect: 'shadow' },
      { type: 'text', text: 'Good things loading...', x: 0.5, y: 0.82, fontSize: 26, fontFamily: 'Inter', fill: '#FFFFFF', textAlign: 'center', width: 0.8, effect: 'shadow' },
    ],
  },
  {
    key: 'photo-crowned',
    label: 'Crowned',
    category: 'photo',
    aspect: '4:5',
    thumbnail: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&h=500&fit=crop&auto=format&q=80',
    background: { type: 'image', value: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=1080&h=1350&fit=crop&auto=format&q=80' },
    layers: [
      { type: 'text', text: '👑 CROWNED', x: 0.5, y: 0.12, fontSize: 52, fontFamily: 'Bebas Neue', fill: '#FFD700', textAlign: 'center', charSpacing: 100, width: 0.85, effect: 'shadow' },
      { type: 'text', text: 'Know your worth', x: 0.5, y: 0.88, fontSize: 24, fontFamily: 'Inter', fill: '#FFFFFF', textAlign: 'center', width: 0.8, effect: 'shadow' },
    ],
  },
  {
    key: 'photo-mogul',
    label: 'Mogul',
    category: 'photo',
    aspect: '1:1',
    thumbnail: 'https://images.unsplash.com/photo-1727702079139-c5e78976dab5?w=400&h=400&fit=crop&auto=format&q=80',
    background: { type: 'image', value: 'https://images.unsplash.com/photo-1727702079139-c5e78976dab5?w=1080&h=1080&fit=crop&auto=format&q=80' },
    layers: [
      { type: 'text', text: 'MOGUL MINDSET', x: 0.5, y: 0.18, fontSize: 48, fontFamily: 'Oswald', fill: '#FFFFFF', textAlign: 'center', charSpacing: 80, fontWeight: '700', width: 0.9, effect: 'shadow' },
      { type: 'text', text: 'Building an empire', x: 0.5, y: 0.82, fontSize: 24, fontFamily: 'Inter', fill: '#E8D5B7', textAlign: 'center', width: 0.75, effect: 'shadow' },
    ],
  },
  {
    key: 'photo-sermon',
    label: 'Sunday Word',
    category: 'photo',
    aspect: '1:1',
    thumbnail: 'https://images.unsplash.com/photo-1765607476376-9574ea76b2ee?w=400&h=400&fit=crop&auto=format&q=80',
    background: { type: 'image', value: 'https://images.unsplash.com/photo-1765607476376-9574ea76b2ee?w=1080&h=1080&fit=crop&auto=format&q=80' },
    layers: [
      { type: 'text', text: '"Your word here"', x: 0.5, y: 0.4, fontSize: 40, fontFamily: 'Playfair Display', fill: '#FFFFFF', textAlign: 'center', fontWeight: '700', width: 0.8, effect: 'shadow' },
      { type: 'text', text: '— Sunday Thoughts', x: 0.5, y: 0.62, fontSize: 22, fontFamily: 'Inter', fill: '#E8D5B7', textAlign: 'center', width: 0.6, effect: 'shadow' },
    ],
  },
  {
    key: 'photo-glow',
    label: 'Glow Up',
    category: 'photo',
    aspect: '9:16',
    thumbnail: 'https://images.unsplash.com/photo-1739825353871-2b9c6716142a?w=400&h=711&fit=crop&auto=format&q=80',
    background: { type: 'image', value: 'https://images.unsplash.com/photo-1739825353871-2b9c6716142a?w=1080&h=1920&fit=crop&auto=format&q=80' },
    layers: [
      { type: 'text', text: 'GLOW UP SZN', x: 0.5, y: 0.35, fontSize: 56, fontFamily: 'Anton', fill: '#FFFFFF', textAlign: 'center', width: 0.85, effect: 'glow' },
    ],
  },

  // --- Graphic / Design Templates ---
  {
    key: 'graphic-bold-drop',
    label: 'Bold Drop',
    category: 'graphic',
    aspect: '1:1',
    background: { type: 'solid', value: '#FF3B30' },
    layers: [
      { type: 'text', text: 'NEW', x: 0.5, y: 0.25, fontSize: 96, fontFamily: 'Anton', fill: '#FFFFFF', textAlign: 'center', charSpacing: 200, width: 0.9 },
      { type: 'text', text: 'DROP', x: 0.5, y: 0.52, fontSize: 96, fontFamily: 'Anton', fill: '#000000', textAlign: 'center', charSpacing: 200, width: 0.9 },
      { type: 'text', text: 'OUT NOW →', x: 0.5, y: 0.82, fontSize: 28, fontFamily: 'Oswald', fill: '#FFFFFF', textAlign: 'center', charSpacing: 120, width: 0.7 },
    ],
  },
  {
    key: 'graphic-split',
    label: 'Split Block',
    category: 'graphic',
    aspect: '1:1',
    background: { type: 'gradient', value: 'linear-gradient(180deg, #F5E6D3 50%, #1a1a1a 50%)' },
    layers: [
      { type: 'text', text: 'YOUR', x: 0.5, y: 0.35, fontSize: 72, fontFamily: 'Bebas Neue', fill: '#1a1a1a', textAlign: 'center', charSpacing: 200, width: 0.8 },
      { type: 'text', text: 'BRAND', x: 0.5, y: 0.65, fontSize: 72, fontFamily: 'Bebas Neue', fill: '#F5E6D3', textAlign: 'center', charSpacing: 200, width: 0.8 },
    ],
  },
  {
    key: 'graphic-podcast',
    label: 'Podcast',
    category: 'graphic',
    aspect: '1:1',
    background: { type: 'gradient', value: 'linear-gradient(135deg, #0a0a0a, #1a1a2e)' },
    layers: [
      { type: 'text', text: '🎙️', x: 0.5, y: 0.22, fontSize: 80, fontFamily: 'Inter', fill: '#FFFFFF', textAlign: 'center', width: 0.3 },
      { type: 'text', text: 'PODCAST NAME', x: 0.5, y: 0.48, fontSize: 40, fontFamily: 'Oswald', fill: '#FFFFFF', textAlign: 'center', charSpacing: 80, fontWeight: '700', width: 0.85 },
      { type: 'text', text: 'EP. 01 — Topic Here', x: 0.5, y: 0.62, fontSize: 22, fontFamily: 'Inter', fill: '#FF9500', textAlign: 'center', width: 0.75 },
      { type: 'text', text: 'Available everywhere you listen', x: 0.5, y: 0.82, fontSize: 16, fontFamily: 'Inter', fill: '#8E8E93', textAlign: 'center', width: 0.8 },
    ],
  },
  {
    key: 'graphic-sale',
    label: 'Flash Sale',
    category: 'graphic',
    aspect: '4:5',
    background: { type: 'gradient', value: 'linear-gradient(135deg, #FFD700, #FF9500, #FF3B30)' },
    layers: [
      { type: 'text', text: 'FLASH', x: 0.5, y: 0.25, fontSize: 80, fontFamily: 'Anton', fill: '#000000', textAlign: 'center', width: 0.85 },
      { type: 'text', text: 'SALE', x: 0.5, y: 0.45, fontSize: 96, fontFamily: 'Anton', fill: '#FFFFFF', textAlign: 'center', width: 0.85, effect: 'shadow' },
      { type: 'text', text: 'UP TO 50% OFF', x: 0.5, y: 0.7, fontSize: 32, fontFamily: 'Oswald', fill: '#000000', textAlign: 'center', charSpacing: 80, width: 0.8 },
      { type: 'text', text: 'Link in bio', x: 0.5, y: 0.85, fontSize: 20, fontFamily: 'Inter', fill: '#000000', textAlign: 'center', width: 0.6 },
    ],
  },
  {
    key: 'graphic-minimalist',
    label: 'Minimalist',
    category: 'graphic',
    aspect: '1:1',
    background: { type: 'solid', value: '#F5F0EB' },
    layers: [
      { type: 'text', text: 'less is more', x: 0.5, y: 0.45, fontSize: 48, fontFamily: 'Playfair Display', fill: '#2C2C2C', textAlign: 'center', fontWeight: '400', width: 0.75 },
      { type: 'text', text: '—', x: 0.5, y: 0.6, fontSize: 36, fontFamily: 'Inter', fill: '#C4A882', textAlign: 'center', width: 0.2 },
    ],
  },
  {
    key: 'graphic-collab',
    label: 'Collab Post',
    category: 'graphic',
    aspect: '1:1',
    background: { type: 'gradient', value: 'linear-gradient(135deg, #1a1a1a, #333333)' },
    layers: [
      { type: 'text', text: 'NAME × NAME', x: 0.5, y: 0.35, fontSize: 44, fontFamily: 'Oswald', fill: '#FFFFFF', textAlign: 'center', charSpacing: 60, fontWeight: '700', width: 0.85 },
      { type: 'text', text: 'COLLABORATION', x: 0.5, y: 0.52, fontSize: 20, fontFamily: 'Inter', fill: '#FFD700', textAlign: 'center', charSpacing: 200, width: 0.8 },
      { type: 'text', text: 'Something special is cooking...', x: 0.5, y: 0.75, fontSize: 22, fontFamily: 'Inter', fill: '#8E8E93', textAlign: 'center', width: 0.8 },
    ],
  },
  {
    key: 'graphic-countdown',
    label: 'Countdown',
    category: 'graphic',
    aspect: '9:16',
    background: { type: 'gradient', value: 'linear-gradient(180deg, #000000, #1a0030, #4a0080)' },
    layers: [
      { type: 'text', text: '3', x: 0.5, y: 0.2, fontSize: 160, fontFamily: 'Anton', fill: '#FFFFFF', textAlign: 'center', width: 0.5, effect: 'glow' },
      { type: 'text', text: 'DAYS LEFT', x: 0.5, y: 0.52, fontSize: 40, fontFamily: 'Oswald', fill: '#AF52DE', textAlign: 'center', charSpacing: 120, width: 0.8 },
      { type: 'text', text: 'Something big is coming', x: 0.5, y: 0.72, fontSize: 24, fontFamily: 'Inter', fill: '#FFFFFF', textAlign: 'center', width: 0.75 },
    ],
  },
];

// Template categories for filtering
export const TEMPLATE_CATEGORIES = [
  { key: 'all',      label: 'All' },
  { key: 'photo',    label: 'Photo' },
  { key: 'graphic',  label: 'Graphic' },
  { key: 'classic',  label: 'Classic' },
  { key: 'quote',    label: 'Quotes' },
  { key: 'reaction', label: 'Reaction' },
  { key: 'story',    label: 'Story' },
  { key: 'announce', label: 'Announce' },
  { key: 'saved',    label: 'My Saved' },
];

/**
 * Get saved personal templates from localStorage.
 */
export function getSavedTemplates() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/**
 * Save current canvas state as a personal template.
 */
export function saveTemplate(name, canvasJSON, aspect, background) {
  const saved = getSavedTemplates();
  const template = {
    key: `saved-${Date.now()}`,
    label: name,
    category: 'saved',
    aspect,
    background,
    canvasJSON,
    createdAt: Date.now(),
  };
  saved.push(template);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
  return template;
}

/**
 * Delete a personal template.
 */
export function deleteTemplate(key) {
  const saved = getSavedTemplates().filter(t => t.key !== key);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
}
